import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { GrUserAdmin } from "react-icons/gr";
import { FaBars } from 'react-icons/fa';
import { logger } from '../utils/logger';
import { useAuth } from '../hooks/useAuth';
import { SearchableDropdown } from './SearchableDropdown';
import { ThemedSpinner } from './ThemedSpinner';
import { useTranslation } from '../localization/useAppTranslation';
import { getVersionString } from '../config/version';
import '../styles/Chat.css';

const moduleLogger = logger.getModule('ChatHeader');
const MAX_VISIBLE_OPTIONS = 10000; // Arbitrary large number to effectively show all options

interface ChatHeaderProps {
  showSidebarToggle?: boolean;
  isSidebarOpen?: boolean;
  onToggleSidebar?: () => void;
  onNewSession: () => void;
}

export const ChatHeader: React.FC<ChatHeaderProps> = ({
  showSidebarToggle = false,
  isSidebarOpen = false,
  onToggleSidebar,
  onNewSession,
}) => {
  const { t } = useTranslation('common');
  const {
    authState,
    session,
    logout,
    selectedFleetId,
    setSelectedFleetId,
    createChatSession,
    isUserInitiatedSessionLoading,
  } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const handleLogout = async () => {
    if (isLoggingOut) {
      return;
    }

    setIsLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      moduleLogger.error('Logout failed', error);
      setIsLoggingOut(false);
    }
  };

  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showUserMenu]);

  const customerOptions = (authState.user?.customers || []).map((customer) => ({
    value: String(customer.id),
    label: customer.name,
  }));

  const fleetOptions = (session?.fleets || []).map((fleet) => ({
    value: String(fleet.id),
    label: `${fleet.name} (${fleet.active_vehicles})`,
    searchText: `${fleet.name} ${fleet.active_vehicles}`,
  }));

  const handleFleetChange = (fleetIdValue: string) => {
    const fleetId = parseInt(fleetIdValue, 10);
    setSelectedFleetId(Number.isNaN(fleetId) ? null : fleetId);
  };

  const handleCustomerChange = async (customerId: string) => {
    try {
      if (!customerId || customerId === String(session?.customerId || '')) {
        return;
      }

      await createChatSession(customerId, null, undefined, undefined, true);
      moduleLogger.debug('Switched customer and created new session', { customerId });
    } catch (error) {
      moduleLogger.error('Failed to switch customer', error);
    }
  };

  // Get fleet name for display
  const getSelectedFleetName = () => {
    const fleet = session?.fleets?.find(f => f.id === selectedFleetId);
    return fleet?.name || t('header.allVehicles');
  };

  return (
    <header className="chat-header">
      {/* Left container: Logo + Selectors */}
      <div className="header-left">
        <div
          className="logo-container"
          onClick={onNewSession}
          style={{ cursor: 'pointer' }}
          title={t('header.newSession')}
        >
          <img src="/images/SpeedGauge_Coach_blue.png" alt="SpeedGauge Safety Center Coach" className="logo-image-large" />
        </div>
        
        <div className="header-selectors" data-tour="scope-controls">
          {showSidebarToggle && onToggleSidebar && (
            <button
              type="button"
              className="header-icon-btn header-sidebar-btn"
              onClick={onToggleSidebar}
              data-tour="mobile-sidebar-toggle"
              aria-label={isSidebarOpen ? t('header.closeSidebar') : t('header.openSidebar')}
              title={isSidebarOpen ? t('header.closeSidebar') : t('header.openSidebar')}
            >
              <FaBars />
            </button>
          )}

          {customerOptions.length > 0 && (
            <SearchableDropdown
              id="customer-select"
              className="header-customer-selector"
              value={String(session?.customerId || authState.user?.customerId || '')}
              options={customerOptions}
              placeholder={t('header.selectCustomer')}
              title={t('header.selectCustomer')}
              maxVisibleOptions={MAX_VISIBLE_OPTIONS}
              onChange={handleCustomerChange}
            />
          )}

          {/* Fleet Selector - in same container as logo */}
          {fleetOptions.length > 0 && (
            <SearchableDropdown
              id="fleet-select"
              className="header-fleet-selector"
              value={selectedFleetId ? String(selectedFleetId) : null}
              options={fleetOptions}
              placeholder={t('header.selectVehicleGroup')}
              maxVisibleOptions={MAX_VISIBLE_OPTIONS}
              title={t('header.selectVehicleGroup')}
              rightAdornment={
                isUserInitiatedSessionLoading
                  ? <ThemedSpinner size="sm" ariaLabel={t('header.loadingFleetList')} />
                  : null
              }
              disabled={isUserInitiatedSessionLoading}
              onChange={handleFleetChange}
            />
          )}
        </div>
      </div>
      
      {/* Right container: User Menu (always on right) */}
      <div className="header-right">
        {/* User Menu Container - positioned relatively for absolute dropdown */}
        <div className="user-menu" ref={userMenuRef}>
          {/* Unified Icon Button Bar */}
          <div className="header-button-group">
            {/* User Menu Button */}
            <button 
              className="header-icon-btn user-menu-button"
              onClick={toggleUserMenu}
              aria-label={t('header.accountMenu')}
              title={t('header.accountAndSettings')}
            >
              <img src="/avatars/user-solid-full.svg" alt="" />
            </button>
          </div>
          
          {/* User Menu Dropdown */}
          {showUserMenu && (
            <div className="user-menu-dropdown">
              {/* User Info Header */}
              <div className="menu-section">
                <div className="menu-header">
                  <strong>{authState.user?.username}</strong>
                </div>
                <div className="menu-info">
                  <span className="info-label">{t('header.customer')}</span>
                  <span className="info-value">{session?.customerName || session?.customerId || authState.user?.customerId}</span>
                </div>
                {session?.fleets && session.fleets.length > 0 && (
                  <div className="menu-info">
                    <span className="info-label">{t('header.vehicleGroup')}</span>
                    <span className="info-value">{getSelectedFleetName()}</span>
                  </div>
                )}
              </div>
              
              {/* Admin Link */}
              {(session?.isSuperuser || session?.permissions?.['speedbot.access_admin_dashboard']) && (
                <>
                  <div className="menu-divider"></div>
                  <Link 
                    to={location.pathname === '/admin' ? '/' : '/admin'}
                    className="menu-item admin-menu-item"
                    onClick={() => setShowUserMenu(false)}
                  >
                    <GrUserAdmin className="admin-icon" />
                    <span>{location.pathname === '/admin' ? t('header.backToChat') : t('header.adminDashboard')}</span>
                  </Link>
                </>
              )}
              
              {/* Logout Button */}
              <div className="menu-divider"></div>
              <button 
                className="menu-item logout-item"
                onClick={handleLogout}
                disabled={isLoggingOut}
              >
                {isLoggingOut ? (
                  <>
                    <ThemedSpinner size="sm" ariaLabel={t('header.loggingOut')} />
                    {t('header.loggingOut')}
                  </>
                ) : (
                  t('header.returnToSafetyCenter')
                )}
              </button>
              <div className="menu-version">Coach AI {getVersionString()}</div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
